import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFacilitators, FacilitatorUsage as Facilitator } from '../../services/api';
import MonitorLayout from './MonitorLayout';
import { InfoDot } from './MetricTile';
import { Period, periodRange, periodReady } from './period';
import PeriodPicker from './PeriodPicker';

// One explanation for the whole card, shown from the ⓘ in its top-right corner.
// All three stats live in a single narrow strip, so three separate tooltips
// would be more to hunt through than one (Athira, 2026-08-19).
const CARD_HINT = (
  <>
    <span className="mb-1.5 block font-bold text-slate-700">What this card shows</span>
    <span className="mb-1 block">
      <b className="font-bold text-slate-700">Active …</b> — the last time they did anything on the app at all.
    </span>
    <span className="mb-1 block">
      <b className="font-bold text-slate-700">Sessions</b> — classes taught in this period. One class on one day = one
      session, counted from them ticking off module content.
    </span>
    <span className="mb-1 block">
      <b className="font-bold text-slate-700">Coach time</b> — time they themselves spent chatting with the coach. A
      chat left open and walked away from is not counted.
    </span>
    <span className="block">
      <b className="font-bold text-slate-700">Attendance</b> — on the days they held a session, how many of their
      students used the coach that day. Blank if they have no students or no sessions yet. For a facilitator working
      in more than one school this is the <b className="font-bold text-slate-700">average across their schools</b>:
      each school is worked out on its own students and its own session days, then the totals are added up and divided
      once. Open the facilitator to see each school on its own card.
    </span>
  </>
);

/** Facilitator-first directory (replaces the old school-grouped pages). A
 *  facilitator can cover more than one school, so we list facilitators flat and
 *  show their school as a tag rather than nesting them under a school. */
// How many schools a facilitator actually works in — their own school plus any
// class assigned to another one. One attendance figure spanning several schools
// is meaningless (a child cannot attend a session held in another school), so
// the card refuses to print one (Athira, 2026-08-26).
// The schools a facilitator is ASSIGNED to. Their registered `school` is not a
// school they "belong" to — with several schools under them there is no own
// school — so it only stands in when they have no classes yet.
const schoolsOf = (f: Facilitator): string[] => {
  const assigned = new Set(
    (f.classes || []).map((c) => c.school || f.school).filter(Boolean) as string[],
  );
  if (assigned.size) return Array.from(assigned).sort();
  return f.school ? [f.school] : [];
};
const schoolCount = (f: Facilitator): number => schoolsOf(f).length;

const DAY = 24 * 60 * 60 * 1000;
const relLabel = (isoStr?: string | null): string => {
  if (!isoStr) return 'Never active';
  const t = new Date(isoStr).getTime();
  if (Number.isNaN(t)) return 'Never active';
  const d = Math.floor((Date.now() - t) / DAY);
  if (d <= 0) return 'Active today';
  if (d === 1) return 'Active yesterday';
  if (d < 7) return `Active ${d}d ago`;
  if (d < 30) return `Active ${Math.floor(d / 7)}w ago`;
  return `Active ${new Date(isoStr).toLocaleDateString()}`;
};
const isActiveToday = (isoStr?: string | null): boolean => {
  if (!isoStr) return false;
  const t = new Date(isoStr).getTime();
  if (Number.isNaN(t)) return false;
  return Math.floor((Date.now() - t) / DAY) <= 0;
};
const fmtMins = (m: number): string => {
  if (!m) return '0m';
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return h ? `${h}h ${mm}m` : `${mm}m`;
};

const FacilitatorDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Facilitator[]>([]);
  const [period, setPeriod] = useState<Period>('all');
  const [customSince, setCustomSince] = useState<string | undefined>(undefined);
  const [customUntil, setCustomUntil] = useState<string | undefined>(undefined);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('monitorToken');
    if (!token) {
      navigate('/monitor/login', { replace: true });
      return;
    }
    if (!periodReady(period, { since: customSince, until: customUntil })) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getFacilitators(token, periodRange(period, { since: customSince, until: customUntil }))
      .then((f) => {
        setRows(f);
        setError(null);
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('monitorToken');
          navigate('/monitor/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, period, customSince, customUntil]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = [...rows].sort((a, b) => b.coach_minutes - a.coach_minutes || b.coach_chats - a.coach_chats);
    if (!s) return base;
    return base.filter((f) => `${f.name || ''} ${f.username} ${f.school || ''}`.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <MonitorLayout>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Facilitators</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Every facilitator across the program. Click one to drill into their schools, classes, and activity.
          </p>
        </div>
        <PeriodPicker
          value={{ period, since: customSince, until: customUntil }}
          onChange={(v) => { setPeriod(v.period); setCustomSince(v.since); setCustomUntil(v.until); }}
        />
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search name, username, or school…"
        className="mb-4 w-full rounded-xl border border-[#E9E6F4] bg-white px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
      />

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : error ? (
        <p className="py-10 text-center text-sm text-rose-600">{error}</p>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-400">No facilitators match.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f) => {
            const active = isActiveToday(f.last_active);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => navigate(`/monitor/facilitators/${f.id}`)}
                className="group relative flex flex-col rounded-2xl border border-[#E9E6F4] bg-white p-5 text-left shadow-sm transition hover:border-violet-300 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-violet-100 text-base font-bold text-violet-700">
                    {(f.name || f.username).slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-bold text-slate-900">{f.name || f.username}</p>
                    <p className="truncate text-xs text-slate-400">@{f.username}</p>
                  </div>
                  <span className="flex flex-none items-center gap-2">
                    <InfoDot hint={CARD_HINT} />
                    <span className="text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-violet-500">→</span>
                  </span>
                </div>

                {/* Last active — the card's headline description */}
                <div className="mt-3 flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  <span className={`text-xs font-semibold ${active ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {relLabel(f.last_active)}
                  </span>
                </div>

                {schoolsOf(f).length > 0 && (
                  <span className="mt-2 flex flex-wrap items-center gap-1">
                    {schoolsOf(f).map((s) => (
                      <span
                        key={s}
                        className="max-w-full truncate rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700"
                      >
                        {s}
                      </span>
                    ))}
                    {schoolCount(f) > 1 && (
                      <span
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500"
                        title={`Assigned to ${schoolCount(f)} schools: ${schoolsOf(f).join(', ')}`}
                      >
                        {schoolCount(f)} schools
                      </span>
                    )}
                  </span>
                )}

                {/* Compact stat strip */}
                <div className="mt-auto grid grid-cols-3 gap-2 border-t border-[#F1EFFA] pt-3 text-center">
                  <Stat value={String(f.sessions_conducted)} label="Sessions" />
                  <Stat value={fmtMins(f.coach_minutes)} label="Coach time" />
                  <Stat
                    value={f.attendance_pct == null ? '—' : `${f.attendance_pct}%`}
                    label={schoolCount(f) > 1 ? 'Attendance · avg' : 'Attendance'}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </MonitorLayout>
  );
};

// The three stats share one narrow strip, so they don't each get their own ⓘ —
// the card's single ⓘ (top-right) explains all three at once, which is also less
// to hunt through than three separate tooltips (Athira, 2026-08-19).
const Stat: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <p className="text-sm font-bold tabular-nums text-slate-800">{value}</p>
    <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
  </div>
);

export default FacilitatorDirectory;
